<script setup>
// NOTICE: KAPTION-INTEGRATION
// Reply box component for Kaption utility templates
// Features: inline editor, template selector modal (controls exposed to parent via defineExpose)
import { computed, ref, watch, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import KaptionInlineUtilityTemplateEditor from './KaptionInlineUtilityTemplateEditor.vue';
import KaptionTemplateSelector from './KaptionTemplateSelector.vue';
import { DEFAULT_TEMPLATE_LANGUAGES } from 'dashboard/helper/kaptionTemplateLanguages';

// NOTICE: KAPTION-INTEGRATION - Ref to access inline editor methods

const props = defineProps({
  /**
   * All available templates organized by type and language
   * { followup: { en: [...], es: [...] }, scheduled: {...} }
   */
  templates: {
    type: Object,
    required: true,
  },
  /**
   * Template type: 'followup', 'scheduled', 'reminder'
   */
  templateType: {
    type: String,
    required: true,
    validator: val => ['followup', 'scheduled', 'reminder'].includes(val),
  },
  /**
   * Preferred language for templates
   */
  language: {
    type: String,
    default: 'en',
  },
  /**
   * template-languages.UI.1 - Languages the account offers agents
   */
  allowedLanguages: {
    type: Array,
    default: () => [...DEFAULT_TEMPLATE_LANGUAGES],
  },
  /**
   * Currently selected template (v-model:template)
   */
  template: {
    type: Object,
    default: null,
  },
  /**
   * Variable value (v-model:variable) - persists across template changes
   */
  variable: {
    type: String,
    default: '',
  },
  /**
   * Disabled state
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether to show search dropdown (controlled by parent)
   */
  showSearch: {
    type: Boolean,
    default: false,
  },
  /**
   * Enable attachment upload feature
   */
  enableAttachments: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:template',
  'update:variable',
  'update:language',
  'update:templateType',
  'update:showSearch',
  'valid',
  'attachmentUploaded',
]);

const { t } = useI18n();

// NOTICE: KAPTION-INTEGRATION - Ref for inline editor to access attachment methods
const inlineEditorRef = useTemplateRef('inlineEditorRef');

// Local state
const localVariable = ref(props.variable);
const selectedLanguage = ref(props.language);
const selectedType = ref(props.templateType);

// Sync variable with prop
watch(
  () => props.variable,
  newVal => {
    localVariable.value = newVal;
  }
);

// Sync language with prop
watch(
  () => props.language,
  newVal => {
    selectedLanguage.value = newVal;
  }
);

// Sync type with prop
watch(
  () => props.templateType,
  newVal => {
    selectedType.value = newVal;
  }
);

// NOTICE: KAPTION-INTEGRATION - template-languages.UI.1: languages the account
// offers that have templates for the current type
const offeredLanguages = computed(() =>
  props.allowedLanguages.length
    ? props.allowedLanguages
    : DEFAULT_TEMPLATE_LANGUAGES
);

const availableLanguages = computed(() => {
  const typeTemplates = props.templates[selectedType.value];
  if (!typeTemplates) return [offeredLanguages.value[0]];
  return offeredLanguages.value.filter(lang => typeTemplates[lang]?.length > 0);
});

// NOTICE: KAPTION-INTEGRATION - Get templates for current type and selected language
// NO fallback to other languages - only show templates in the selected language
const availableTemplates = computed(() => {
  const typeTemplates = props.templates[selectedType.value];
  if (!typeTemplates) return [];

  // ONLY return templates in the selected language - NO fallback
  return typeTemplates[selectedLanguage.value] || [];
});

// Current template index
const currentIndex = computed(() => {
  if (!props.template) return -1;
  return availableTemplates.value.findIndex(
    tpl => tpl.name === props.template.name
  );
});

// Navigation
const canGoPrev = computed(() => currentIndex.value > 0);
const canGoNext = computed(
  () => currentIndex.value < availableTemplates.value.length - 1
);

const goToPrev = () => {
  if (canGoPrev.value) {
    const newTemplate = availableTemplates.value[currentIndex.value - 1];
    emit('update:template', newTemplate);
  }
};

const goToNext = () => {
  if (canGoNext.value) {
    const newTemplate = availableTemplates.value[currentIndex.value + 1];
    emit('update:template', newTemplate);
  }
};

// Select template from modal
const selectTemplate = template => {
  emit('update:template', template);
};

// Handle language change (called from parent)
const changeLanguage = newLang => {
  selectedLanguage.value = newLang;
  emit('update:language', newLang);

  // Auto-select first template in new language, or null if none exist
  const typeTemplates = props.templates[selectedType.value];
  const templates = typeTemplates?.[newLang] || [];
  if (templates.length > 0) {
    emit('update:template', templates[0]);
  } else {
    emit('update:template', null);
  }
};

// Handle variable update - persists value
const handleVariableUpdate = value => {
  localVariable.value = value;
  emit('update:variable', value);
};

// Forward validity from editor
const handleValid = valid => {
  emit('valid', valid);
};

// Forward attachment uploaded event
const handleAttachmentUploaded = attachment => {
  emit('attachmentUploaded', attachment);
};

// Template counter text
const templateCounterText = computed(() => {
  if (availableTemplates.value.length === 0) return '';
  const current = currentIndex.value + 1;
  const total = availableTemplates.value.length;
  return `${current}/${total}`;
});

// Close modal
const closeSearch = () => {
  emit('update:showSearch', false);
};

// NOTICE: KAPTION-INTEGRATION - Handle template type change from modal
const handleTypeChange = newType => {
  selectedType.value = newType;
  emit('update:templateType', newType);
};

// NOTICE: KAPTION-INTEGRATION - Attachment methods delegated to inline editor
const triggerFilePicker = () => {
  inlineEditorRef.value?.triggerFilePicker();
};

const isUploadingAttachment = computed(
  () => inlineEditorRef.value?.isUploading || false
);

// Expose controls to parent component via ref
defineExpose({
  // Navigation
  canGoPrev,
  canGoNext,
  goToPrev,
  goToNext,
  // Language
  availableLanguages,
  selectedLanguage,
  changeLanguage,
  // Type
  selectedType,
  handleTypeChange,
  // Search
  closeSearch,
  // Counter
  templateCounterText,
  // NOTICE: KAPTION-INTEGRATION - Attachment controls
  triggerFilePicker,
  isUploadingAttachment,
});
</script>

<template>
  <div class="kaption-utility-template-reply-box">
    <!-- Template Selector Modal -->
    <KaptionTemplateSelector
      :show="showSearch"
      :templates="templates"
      :template-type="selectedType"
      :selected-template="template"
      :language="selectedLanguage"
      :allowed-languages="offeredLanguages"
      @close="closeSearch"
      @select="selectTemplate"
      @change-language="changeLanguage"
      @change-type="handleTypeChange"
    />

    <!-- Inline Editor (main content area) -->
    <div class="editor-container">
      <!-- NOTICE: KAPTION-INTEGRATION - Added ref for attachment control access from bottom panel -->
      <KaptionInlineUtilityTemplateEditor
        v-if="template"
        ref="inlineEditorRef"
        :template="template"
        :model-value="localVariable"
        :disabled="disabled"
        :enable-attachments="enableAttachments"
        @update:model-value="handleVariableUpdate"
        @valid="handleValid"
        @attachment-uploaded="handleAttachmentUploaded"
      />

      <div v-else class="no-template-selected">
        <Icon icon="i-lucide-file-text" class="size-6 text-n-slate-9" />
        <span>{{
          t('KAPTION_UTILITY_TEMPLATES.SELECT_TEMPLATE', 'Select a template')
        }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.kaption-utility-template-reply-box {
  @apply flex flex-col;
}

/* Editor Container */
.editor-container {
  @apply flex-1;
  min-height: 100px; /* Ensure editor is always visible */

  :deep(.kaption-inline-utility-editor) {
    @apply rounded-none;
  }

  :deep(.template-editor) {
    @apply rounded-none border-0;
    /* Light background to ensure text is visible */
    background: rgb(var(--solid-1) / 0.5);
    min-height: 80px;
  }
}

.no-template-selected {
  @apply flex flex-col items-center justify-center gap-2 py-8;
  @apply text-sm text-n-slate-10;
}
</style>
